/**
 * Field Visibility Helpers
 * 
 * Utility functions for working with field visibility settings.
 */

import { FIELD_DEFINITIONS, CATEGORY_ORDER } from './definitions';
import { getInternalFieldId } from './idMapping';

/**
 * Get fields for a specific view
 */
export const getFieldsForView = (view) => {
  return FIELD_DEFINITIONS.filter(field => field.views.includes(view));
};

/**
 * Get fields grouped by category for a specific view
 */
export const getFieldsByCategoryForView = (view) => {
  const fields = getFieldsForView(view);
  const grouped = {};
  
  CATEGORY_ORDER.forEach(category => {
    const categoryFields = fields.filter(f => f.category === category);
    if (categoryFields.length > 0) {
      grouped[category] = categoryFields;
    }
  });
  
  return grouped;
};

/**
 * Check if a field is mandatory for a specific view
 */
export const isFieldMandatory = (fieldId, view) => {
  const field = FIELD_DEFINITIONS.find(f => f.id === fieldId);
  return field?.mandatory?.includes(view) ?? false;
};

/**
 * Get mandatory fields for a specific view
 */
export const getMandatoryFieldsForView = (view) => {
  return FIELD_DEFINITIONS.filter(
    field => field.views.includes(view) && field.mandatory?.includes(view)
  );
};

/**
 * Get optional (toggleable) fields for a specific view
 */
export const getOptionalFieldsForView = (view) => {
  return FIELD_DEFINITIONS.filter(
    field => field.views.includes(view) && !field.mandatory?.includes(view)
  );
};

/**
 * Get default enabled state for a field in a specific view
 */
export const getFieldDefaultEnabled = (field, view) => {
  if (typeof field.defaultEnabled === 'object') {
    return field.defaultEnabled[view] ?? false;
  }
  return field.defaultEnabled ?? false;
};

/**
 * Whether a built-in field is visible: stored false hides, stored true shows,
 * missing falls back to that field's default for the view.
 * Custom fields are opt-in and should use `stored === true` instead.
 */
export const isBuiltInFieldVisible = (storedValue, field, view) => {
  if (storedValue === false) return false;
  if (storedValue === true) return true;
  return getFieldDefaultEnabled(field, view);
};

/**
 * Generate default visibility settings for a specific view
 * Returns settings with INTERNAL field IDs (for backward compatibility)
 */
export const getDefaultVisibilityForView = (view) => {
  const fields = getFieldsForView(view);
  const defaults = {};
  
  fields.forEach(field => {
    const internalId = getInternalFieldId(field.id, view);
    
    if (field.mandatory?.includes(view)) {
      defaults[internalId] = true;
    } else {
      defaults[internalId] = getFieldDefaultEnabled(field, view);
    }
  });
  
  return defaults;
};

/**
 * Merge saved settings with defaults for a view
 */
export const mergeWithDefaultsForView = (savedSettings, view) => {
  const migrated = savedSettings && typeof savedSettings === 'object' ? { ...savedSettings } : {};
  if (view === 'todolist') {
    if (migrated.estimatedDays !== undefined && migrated.estimatedDurationMinutes === undefined) {
      migrated.estimatedDurationMinutes = migrated.estimatedDays;
    }
    delete migrated.estimatedDays;
  }
  const defaults = getDefaultVisibilityForView(view);
  return { ...defaults, ...migrated };
};

/**
 * Get field label by ID (optionally view-specific)
 */
export const getFieldLabel = (fieldId, view = null) => {
  const field = FIELD_DEFINITIONS.find(f => f.id === fieldId);
  if (!field) return fieldId;
  
  if (view && field.labelByView && field.labelByView[view]) {
    return field.labelByView[view];
  }
  
  return field.label ?? fieldId;
};

/**
 * Get field with view-specific label applied
 */
export const getFieldWithLabel = (field, view) => {
  if (!field) return field;
  
  const label = (field.labelByView && field.labelByView[view]) 
    ? field.labelByView[view] 
    : field.label;
  
  return { ...field, label };
};

// View-specific defaults (pre-computed for convenience)
export const DEFAULT_TABLE_VISIBILITY = getDefaultVisibilityForView('table');
export const DEFAULT_FLOW_VISIBILITY = getDefaultVisibilityForView('flow');
export const DEFAULT_KANBAN_VISIBILITY = getDefaultVisibilityForView('kanban');
export const DEFAULT_TODOLIST_VISIBILITY = getDefaultVisibilityForView('todolist');
